import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Typography,
    Button,
    Radio
} from "@mui/material/";
import { helpers, format } from "../../../../../common/lib";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../../../Store/actions";
import { strings } from "../../../../../common/i18n";
import MatchCard from "./MatchCard";
import CloseIcon from "@mui/icons-material/Close";

const getMatchingFields = (current = {}, candidate = {}) => {
    return Object.keys(candidate).filter(
        (key) =>
            current[key] && candidate[key] && current[key] === candidate[key]
    );
};

const Section = ({ title, children }) => (
    <Box mb={4}>
        <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1 }}
            color="primary"
        >
            {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {children}
    </Box>
);

const MatchDrawer = ({ open, onClose, asset, onConfirmMatch }) => {
    const [selectedMatchId, setSelectedMatchId] = useState(null);
    const {
        altAssets,
        assets,
        auditLocations,
        auth,
        locations,
        typesList,
        branches
    } = useSelector((state) => state);
    const dispatch = useDispatch();

    useEffect(() => {
        setSelectedMatchId(null);
    }, [asset]);

    if (!asset || asset.length === 0) {
        return null;
    }

    const {
        identifier1,
        identifier2,
        make,
        type,
        suggestedMatch,
        assetMatches = []
    } = asset[0];

    const currentData = {
        vin: identifier1,
        reg: identifier2,
        make,
        type
    };

    const suggested = suggestedMatch?.asset
        ? {
              vin: suggestedMatch.asset.identifier1,
              reg: suggestedMatch.asset.identifier2,
              make: helpers.getListItemForId(
                  typesList.makes,
                  suggestedMatch.asset.make
              ).name,
              type: helpers.getListItemForId(
                  typesList.assetTypes,
                  suggestedMatch.asset.type
              ).name,
              model: suggestedMatch.asset.model,
              expected: suggestedMatch.auxiliary?.isExpected ? "Yes" : "No",
              live: suggestedMatch.auxiliary?.isLive ? "Yes" : "No",
              createdDate: format.dateTimeNew(
                  suggestedMatch.auxiliary?.createdDate
              ),
              location: helpers.getListItemForId(
                  locations,
                  suggestedMatch.auxiliary?.location
              ).name,
              branch: helpers.getListItemForId(
                  branches,
                  suggestedMatch.auxiliary?.branch
              ).name
          }
        : null;

    const handleAcceptMatch = () => {
        let selectedMatch = null;

        if (selectedMatchId === "suggested") {
            selectedMatch = suggestedMatch;
        } else if (selectedMatchId?.startsWith("asset-")) {
            const index = parseInt(selectedMatchId.split("-")[1], 10);
            selectedMatch = assetMatches[index];
        }
        if (!selectedMatch) return;

        const res = altAssets.data.find((item) => item.id === asset[0].id);
        const updatedAltAsset = {
            ...res,
            isMatched: true,
            asset: selectedMatch.asset,
            assetAuxiliary: selectedMatch.auxiliary
        };

        dispatch(actions.altAssets.create.updateStore([updatedAltAsset]));

        onConfirmMatch(selectedMatch);
        onClose();
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box
                sx={{
                    width: 500,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative"
                }}
            >
                {/* Top Content */}
                <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
                    <IconButton
                        onClick={onClose}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h6" gutterBottom>
                        Resolve Asset Match
                    </Typography>

                    <Box mt={4}>
                        <Section title="Current">
                            <MatchCard
                                data={currentData}
                                matchTo={currentData}
                            />
                        </Section>

                        {suggested && (
                            <Section title="Suggested Match">
                                <MatchCard
                                    data={suggested}
                                    matchTo={currentData}
                                    selected={selectedMatchId === "suggested"}
                                    onClick={() =>
                                        setSelectedMatchId("suggested")
                                    }
                                    showRadio
                                    matchFields={getMatchingFields(
                                        currentData,
                                        suggested
                                    )}
                                />
                            </Section>
                        )}

                        {assetMatches.length > 0 && (
                            <Section title="Asset Matches">
                                <Grid container direction="column">
                                    {assetMatches.map((match, idx) => {
                                        const assetData = {
                                            id: asset.id,
                                            vin: match.asset.identifier1,
                                            reg: match.asset.identifier2,
                                            make: helpers.getListItemForId(
                                                typesList.makes,
                                                match.asset.make
                                            ).name,
                                            type: helpers.getListItemForId(
                                                typesList.assetTypes,
                                                match.asset.type
                                            ).name,
                                            model: match.asset.model,
                                            expected: match.auxiliary
                                                ?.isExpected
                                                ? "Yes"
                                                : "No",
                                            live: match.auxiliary?.isLive
                                                ? "Yes"
                                                : "No",
                                            createdDate: format.dateTimeNew(
                                                match.auxiliary?.createdDate
                                            ),
                                            location: helpers.getListItemForId(
                                                locations,
                                                match.auxiliary?.location
                                            ).name,
                                            branch: helpers.getListItemForId(
                                                branches,
                                                match.auxiliary?.branch
                                            ).name
                                            // location: match.auxiliary?.location,
                                            // branch: match.auxiliary?.branch
                                        };

                                        const matchId = `asset-${idx}`;

                                        return (
                                            <MatchCard
                                                key={match.asset.id || matchId}
                                                data={assetData}
                                                matchTo={currentData}
                                                selected={
                                                    selectedMatchId === matchId
                                                }
                                                onClick={() =>
                                                    setSelectedMatchId(matchId)
                                                }
                                                showRadio
                                                matchFields={getMatchingFields(
                                                    currentData,
                                                    assetData
                                                )}
                                            />
                                        );
                                    })}
                                </Grid>
                            </Section>
                        )}
                    </Box>
                </Box>

                {/* Bottom Actions */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: "1px solid #e0e0e0",
                        display: "flex",
                        justifyContent: "space-between",
                        backgroundColor: "#fff",
                        position: "sticky",
                        bottom: 0
                    }}
                >
                    <Button onClick={onClose} variant="outlined">
                        {strings("close")}
                    </Button>
                    <Button
                        onClick={handleAcceptMatch}
                        variant="contained"
                        disabled={!selectedMatchId}
                    >
                        Accept Match
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

export default MatchDrawer;
